import { apiCall } from "../base";
import { HttpMethod } from "@/types/https";
import {
  MachineResponse,
  MachineGroupedByShopResponse,
  MachineListGroupedByShopParams,
  MachineListParams,
  CreateMachinePayload,
  MachineConflictResponse,
  MachineScheduleResponse,
  UpdateMachineShiftCommand,
  BatchMachineUpdateCommand,
  MachineScheduleDayDto
} from "../types/machines.type";
import {
  PaginatedResponseList,
  PaginatedResponseObject,
  ResourceAvailabilityResponse
} from "../types/schedule.type";
import { ENDPOINT } from "../endpoint";

export const MachineList = (params: MachineListParams) => {
  const stringParams = JSON.stringify(params);
  return {
    name: `MachineList_${stringParams}`,
    handle: (): Promise<MachineResponse> =>
      apiCall<MachineResponse>({
        params,
        url: ENDPOINT.MACHINES,
        method: HttpMethod.GET
      })
  };
};

export const MachineDetails = (machineId: string) => {
  return {
    name: `MachineDetails_${machineId}`,
    handle: (): Promise<MachineResponse> =>
      apiCall<MachineResponse>({
        url: `${ENDPOINT.MACHINES}/${machineId}`,
        method: HttpMethod.GET
      })
  };
};

export const MachineListGroupedByShop = (
  params: MachineListGroupedByShopParams
) => {
  const stringParams = JSON.stringify(params);
  return {
    name: `MachineListGroupedByShop_${stringParams}`,
    handle: (): Promise<MachineGroupedByShopResponse> =>
      apiCall<MachineGroupedByShopResponse>({
        params,
        url: `${ENDPOINT.MACHINES}/grouped-by-shop`,
        method: HttpMethod.GET
      })
  };
};

export const CreateMachine = () => ({
  name: "CreateMachine",
  handle: (machineData: CreateMachinePayload): Promise<MachineResponse> =>
    apiCall<MachineResponse>({
      url: ENDPOINT.MACHINES,
      method: HttpMethod.POST,
      body: machineData
    })
});

export const UpdateMachine = () => ({
  name: "UpdateMachine",
  handle: ({
    machineId,
    body
  }: {
    machineId: string;
    body: any;
  }): Promise<MachineResponse> =>
    apiCall<MachineResponse>({
      url: `${ENDPOINT.MACHINES}/${machineId}`,
      method: HttpMethod.PUT,
      body
    })
});

export const DeleteMachine = () => ({
  name: "DeleteMachine",
  handle: (machineId: string): Promise<MachineResponse> =>
    apiCall<MachineResponse>({
      url: `${ENDPOINT.MACHINES}/${machineId}`,
      method: HttpMethod.DELETE
    })
});

export const UpdateMachineStatus = () => ({
  name: "UpdateMachineStatus",
  handle: ({
    machineId,
    status
  }: {
    machineId: number;
    status: number;
  }): Promise<MachineResponse> =>
    apiCall<MachineResponse>({
      url: `${ENDPOINT.MACHINES}/${machineId}/status`,
      method: HttpMethod.PATCH,
      body: { status }
    })
});

// ─── Machine Schedule API ─────────────────────────────────────────────────────

export const MachineConflicts = (params: {
  machineId: number;
  startDate: string;
  endDate: string;
  excludeWorkflowId?: number;
}) => {
  const { machineId, ...rest } = params;
  const stringParams = JSON.stringify(rest);
  return {
    name: `MachineConflicts_${machineId}_${stringParams}`,
    handle: (): Promise<MachineConflictResponse> =>
      apiCall<MachineConflictResponse>({
        url: `${ENDPOINT.MACHINES}/${machineId}/conflicts`,
        method: HttpMethod.GET,
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          excludeWorkflowId: params.excludeWorkflowId
        }
      })
  };
};

export const GetMachineScheduleRange = (
  machineId: number,
  params: { startDate: string; endDate: string; includeHolidays?: boolean }
) => ({
  name: `MachineScheduleRange_${machineId}_${params.startDate}_${params.endDate}`,
  handle: (): Promise<MachineScheduleResponse> =>
    apiCall<MachineScheduleResponse>({
      url: `${ENDPOINT.MACHINES}/${machineId}/schedule`,
      method: HttpMethod.GET,
      params
    })
});

// Bulk per-machine AM/PM availability for the visible roster + range (WOR-2187).
// POST that reads — cached via useGetData keyed on the name below.
export const GetMachineAvailability = (params: {
  machineIds: number[];
  startDate: string;
  endDate: string;
}) => ({
  name: `MachineAvailability_${params.startDate}_${params.endDate}_${params.machineIds
    .slice()
    .sort((a, b) => a - b)
    .join("-")}`,
  handle: (): Promise<ResourceAvailabilityResponse> =>
    apiCall<ResourceAvailabilityResponse>({
      url: `${ENDPOINT.MACHINES}/schedule/availability`,
      method: HttpMethod.POST,
      body: {
        machineIds: params.machineIds,
        startDate: params.startDate,
        endDate: params.endDate
      }
    })
});

export const UpdateMachineSingleShift = (machineId: number) => ({
  name: `UpdateMachineSingleShift_${machineId}`,
  handle: (
    body: UpdateMachineShiftCommand
  ): Promise<PaginatedResponseObject<MachineScheduleDayDto>> =>
    apiCall<PaginatedResponseObject<MachineScheduleDayDto>>({
      url: `${ENDPOINT.MACHINES}/${machineId}/schedule/shift`,
      method: HttpMethod.POST,
      body
    })
});

export const UpdateMachineScheduleBatch = (machineId: number) => ({
  name: `UpdateMachineScheduleBatch_${machineId}`,
  handle: (
    body: BatchMachineUpdateCommand
  ): Promise<PaginatedResponseList<MachineScheduleDayDto>> =>
    apiCall<PaginatedResponseList<MachineScheduleDayDto>>({
      url: `${ENDPOINT.MACHINES}/${machineId}/schedule/batch`,
      method: HttpMethod.POST,
      body
    })
});

export const DeleteMachineShiftOverride = (machineId: number) => ({
  name: `DeleteMachineShiftOverride_${machineId}`,
  handle: (params: {
    date: string;
    shiftType: "AM" | "PM" | "OT";
    includeHolidays?: boolean;
  }): Promise<PaginatedResponseObject<MachineScheduleDayDto>> =>
    apiCall<PaginatedResponseObject<MachineScheduleDayDto>>({
      url: `${ENDPOINT.MACHINES}/${machineId}/schedule/shift`,
      method: HttpMethod.DELETE,
      params
    })
});
